import { initializeApp } from "firebase/app";
import {
  getDatabase,
  ref,
  set,
  get,
  update,
  child,
  push,
  onChildAdded,
} from "firebase/database";
import firebaseConfig from "./firebaseConfig";
import { getOpponentData } from "@/firebase/userInfo";
import { GameInfo } from "@/stores/gameStore";

const app = initializeApp(firebaseConfig);

export const database = getDatabase(
  app,
  "https://iprog-project-d06b3-default-rtdb.europe-west1.firebasedatabase.app/"
);

/** Create a new game with userID as one of the sides. If 'open'
 * is set to true, the game will be added to the list of 'open
 * games' that require an opponent.
 */
export async function createNewGame(userID: string, open: boolean) {
  // Whether the person that starts the game is the attacker
  const userAsAttacker = Math.random() > 0.5;
  const initialData = {
    attackerID: userAsAttacker ? userID : null,
    defenderID: userAsAttacker ? null : userID,
    moves: [],
  };
  const gameID = push(child(ref(database), "openGames")).key!;
  const updates: { [char: string]: object | string } = {};
  if (open)
    updates["openGames/" + gameID] = new Date(Date.now()).toDateString();
  updates["games/" + gameID] = initialData;
  return update(ref(database), updates).then(() => gameID);
}

/** A function that checks whether there is an open game waiting
 * for an opponent. Returns the game ID if there is, otherwise null.
 */
export async function findOpenGame(userID: string): Promise<string | null> {
  return get(ref(database, "openGames/")).then((data) => {
    if (!data.hasChildren()) return null;
    const firstChild = data.val() as { [char: string]: string };
    const key = Object.keys(firstChild)[0];
    return fetchGame(key).then((gameInfo) => {
      if (!gameInfo) return null;
      if (
        gameInfo["attackerID"] === userID ||
        gameInfo["defenderID"] === userID
      ) {
        return key;
      }
      set(ref(database, `openGames/${key}`), null);
      return key; // Game ID of oldest open game
    });
  });
}

export async function joinOpenGame(gameID: string, userID: string) {
  return set(ref(database, `openGames/${gameID}`), null).then(() => {
    return joinClosedGame(gameID, userID);
  });
}

export async function joinClosedGame(gameStore: any, userStore: any) {
  const gameID = gameStore.gameId;
  const userID = userStore.uid;
  return fetchGame(gameID)
    .then((gameInfo) => {
      if (gameInfo == null) return;
      if (
        gameInfo["attackerID"] === userID ||
        gameInfo["defenderID"] === userID
      )
        return gameInfo;
      console.log("Joining closed game with info: ", gameInfo);
      if (gameInfo["attackerID"] == undefined) {
        set(ref(database, `games/${gameID}/attackerID`), userID);
        return { ...gameInfo, attackerID: userID };
      } else if (gameInfo["defenderID"] == undefined) {
        set(ref(database, `games/${gameID}/defenderID`), userID);
        return { ...gameInfo, defenderID: userID };
      }
    })
    .then((gameInfo: GameInfo | undefined) => {
      if (!gameInfo) {
        gameStore.setErrorMesaage(
          `Game with ID ${gameID} full or does not exist`
        );
        gameStore.setWaitingMessage("");
        return;
      }
      if (!gameInfo.attackerID || !gameInfo.defenderID) {
        console.log("Created a new game. Waiting for an opponent");
        gameStore.setWaitingMessage("Waiting for an opponent...");
        const opponentListenerRemover = opponentFoundListener(
          gameID,
          (opponentRole: string, opponentID: string) => {
            if (gameInfo[opponentRole as keyof GameInfo]) return;
            gameStore.setGameInfo(gameInfo, opponentID);
            gameStore.setOpponentInfo(opponentID);
            getOpponentData(gameStore);
            gameStore.initializeOnlineGame();
            opponentListenerRemover();
          }
        );
        return;
      }
      console.log("Joining an existing game.");
      gameStore.setGameInfo(gameInfo);
      const opponentID =
        gameInfo["defenderID"] === userID
          ? gameInfo["attackerID"]
          : gameInfo["defenderID"];
      gameStore.setOpponentInfo(opponentID.toString());
      getOpponentData(gameStore);
      gameStore.initializeOnlineGame();
    });
}

export async function fetchGame(
  gameID: string
): Promise<{ [char: string]: object | string } | null> {
  return get(ref(database, `games/${gameID}`)).then((data) => {
    console.log("Fetching game: ", data.val());
    if (!data.exists()) {
      console.log("Could not load game data for id " + gameID);
      return null;
    }
    const gameInfo = data.val() as { [char: string]: object | string };
    return gameInfo;
  });
}

export function opponentFoundListener(
  gameID: string,
  opponentFoundHandler: CallableFunction
) {
  console.log("Searching for an opponent to game " + gameID);
  const openGamesRef = ref(database, `games/${gameID}`);
  return onChildAdded(openGamesRef, (data) => {
    console.log("Received player role: ", data.key);
    opponentFoundHandler(data.key, data.val());
  });
}
